import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns/format';
import { ListChecks, MessageSquarePlus } from 'lucide-react';
import {
  FEEDBACK_KIND_LABELS,
  feedbackStatusLabel,
  screenOfPath,
  feedbackConfirmationLabel,
  feedbackUuid,
  createFeedbackConfirmation,
  reportForCompletion,
} from '@cerebro-diem/core';
import type { FeedbackStatus, FeedbackCompletion } from '@cerebro-diem/core';
import {
  listMyFeedback,
  listMyFeedbackCompletions,
  confirmFeedback,
  APP_VERSION,
} from '../../services/feedback';
import { errorMessage } from '../../services/errors';
import { LIST_LIMIT, queryKeys, reachedLimit } from '../../services/queries';
import { useFeedbackDialog } from '../../stores/feedbackStore';
import { outlineButton } from '../../components/styles';

import AboutApp from '../settings/AboutApp';

const STATUS_STYLE: Record<FeedbackStatus, string> = {
  new: 'bg-surface-variant text-on-surface-variant',
  ticketed: 'bg-primary-container text-primary',
  done: 'bg-success-container text-on-success-container',
  wont_do: 'bg-surface-variant text-on-surface-variant',
};

/** One mounted editor per account and completion; retries retain the exact first submission. */
function CompletionCard({
  completion,
  ownerId,
  message,
  reportError,
}: {
  completion: FeedbackCompletion;
  ownerId: string;
  message?: string;
  reportError: (id: string, message: string | null) => void;
}) {
  const client = useQueryClient();
  const [editor] = useState(() =>
    createFeedbackConfirmation({
      ownerId,
      completionId: completion.completion_id,
      platform: 'web',
      appVersion: APP_VERSION,
      currentOwner: () => useFeedbackDialog.getState().ownerId,
      newId: () => crypto.randomUUID(),
      confirm: confirmFeedback,
      invalidate: (owner, includeReport) =>
        client.invalidateQueries(
          {
            queryKey: includeReport
              ? [...queryKeys.feedback, owner]
              : [...queryKeys.feedback, owner, 'completions'],
          },
          { throwOnError: true },
        ),
    }),
  );
  const { works, note, sending, saved, error, refreshError, request } =
    useSyncExternalStore(editor.subscribe, editor.getSnapshot);
  const errorText = [error, refreshError]
    .filter((failure) => failure !== null)
    .map((failure) =>
      errorMessage(failure.cause),
    )
    .join(' · ');
  useEffect(() => {
    reportError(completion.completion_id, errorText || null);
  }, [completion.completion_id, errorText, reportError]);
  const answerable = completion.active && !completion.confirmation_id && !saved;
  return (
    <article
      id={`completion-${completion.completion_id}`}
      tabIndex={-1}
      className="rounded-xl border border-outline/20 p-4 space-y-3"
    >
      <h3 className="font-medium">Test request · {completion.ticket}</h3>
      <p className="text-sm">
        Completed {format(new Date(completion.completed_at), 'MMM d, yyyy')}
      </p>
      {message && <p className="whitespace-pre-wrap break-words">{message}</p>}
      <p role="status">
        {saved && !completion.confirmation_id
          ? 'Saved · Refreshing ticket delivery…'
          : feedbackConfirmationLabel(completion)}
      </p>
      {completion.note !== null && (
        <p className="whitespace-pre-wrap break-words">{completion.note}</p>
      )}
      {completion.tested_app_version && (
        <p>
          Tested {completion.tested_platform} · {completion.tested_app_version}
        </p>
      )}
      {answerable && (
        <>
          <p>
            Try the original action on your current build, then tell us what
            happened.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className={outlineButton}
              aria-pressed={works === true}
              disabled={!!request || sending}
              onClick={() => editor.setWorks(true)}
            >
              Works
            </button>
            <button
              type="button"
              className={outlineButton}
              aria-pressed={works === false}
              disabled={!!request || sending}
              onClick={() => editor.setWorks(false)}
            >
              Still broken
            </button>
          </div>
          <textarea
            aria-label="Test notes"
            placeholder="Test notes (optional)"
            value={note}
            disabled={!!request || sending}
            onChange={(event) => editor.setNote(event.target.value)}
            className="w-full rounded border p-2 bg-surface"
          />
          {request && (
            <p className="text-sm">Retry sends the same answer and notes.</p>
          )}
          <button
            type="button"
            className={outlineButton}
            disabled={works === null || sending}
            onClick={() => void editor.send(completion)}
          >
            {sending ? 'Sending…' : 'Send confirmation'}
          </button>
        </>
      )}
    </article>
  );
}

/**
 * Your feedback (CER-66): everything the signed-in user has sent, newest
 * first, each with where it stands: New, Ticketed · CER-123, Done, or Won't
 * do with the reason. The team changes the status; nothing here edits it.
 */
export default function FeedbackListPage() {
  const ownerId = useFeedbackDialog((state) => state.ownerId);
  const location = useLocation();
  return <FeedbackHistory key={`${ownerId}:${location.key}`} />;
}

function FeedbackHistory() {
  const location = useLocation();
  const {
    open: openFeedback,
    pending,
    retry,
    sending,
    storageError,
    sendError,
    ownerId,
  } = useFeedbackDialog();
  // Errors derive from the mounted editors, outside the freshness-hidden cards.
  const [confirmationErrors, setConfirmationErrors] = useState<
    Record<string, string | null>
  >({});
  const reportError = useCallback((id: string, message: string | null) => {
    setConfirmationErrors((current) =>
      current[id] === message ? current : { ...current, [id]: message },
    );
  }, []);
  const requested = new URLSearchParams(location.search).get('completion');
  const query = useQuery({
    queryKey: [...queryKeys.feedback, ownerId],
    queryFn: () => listMyFeedback(ownerId!),
    enabled: !!ownerId,
    refetchOnMount: requested ? 'always' : true,
  });
  const completions = useQuery({
    queryKey: [...queryKeys.feedback, ownerId, 'completions'],
    queryFn: () => listMyFeedbackCompletions(ownerId!),
    enabled: !!ownerId,
    refetchOnMount: requested ? 'always' : true,
  });
  const { data, error, isPending } = query;
  const ready =
    !!ownerId &&
    query.isSuccess &&
    completions.isSuccess &&
    !query.isFetching &&
    !completions.isFetching;
  const selected =
    ready && feedbackUuid(requested)
      ? completions.data?.find((row) => row.completion_id === requested)
      : undefined;
  useEffect(() => {
    if (selected) {
      document.getElementById(`completion-${selected.completion_id}`)?.focus();
    } else if (
      requested &&
      document.activeElement instanceof HTMLElement &&
      document.activeElement.id.startsWith('completion-')
    ) {
      document.activeElement.blur();
    }
  }, [selected, ownerId, requested]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <ListChecks className="w-8 h-8 text-primary" />
        <h1 className="text-2xl font-bold">Your feedback</h1>
      </div>
      <p className="text-on-surface-variant">
        Everything you have sent, and what happened to it: each one becomes a
        ticket for the team, or is closed with a reason.
      </p>
      <button
        type="button"
        onClick={() => openFeedback(screenOfPath(location.pathname))}
        className={`${outlineButton} inline-flex items-center gap-2`}
      >
        <MessageSquarePlus className="w-4 h-4" />
        Send feedback
      </button>

      {storageError && (
        <p role="alert" className="text-sm text-error">
          {storageError}
        </p>
      )}
      {sendError && (
        <p role="alert" className="text-sm text-error">
          {sendError}
        </p>
      )}
      {pending.length > 0 && (
        <section className="space-y-3" aria-label="Waiting feedback">
          <p>
            Waiting to send. These reports are kept on this device and retry
            when the app is open and connected.
          </p>
          <button
            type="button"
            className={outlineButton}
            disabled={sending}
            onClick={() => void retry()}
          >
            {sending ? 'Sending…' : 'Retry'}
          </button>
          {pending
            .filter((entry) => !data?.some((item) => item.id === entry.id))
            .map((entry) => (
              <div
                key={entry.id}
                className="rounded-xl border border-outline/20 p-4 space-y-2"
              >
                <span className="font-medium">
                  {FEEDBACK_KIND_LABELS[entry.row.kind]} · Waiting to send
                </span>
                <p className="whitespace-pre-wrap break-words">
                  {entry.row.message}
                </p>
              </div>
            ))}
        </section>
      )}
      {Object.entries(confirmationErrors).map(
        ([id, message]) =>
          message && (
            <p key={id} role="alert" className="text-sm text-error">
              {message}
            </p>
          ),
      )}
      {completions.error && (
        <p role="alert">
          Your test requests could not be loaded:{' '}
          {errorMessage(completions.error)}
        </p>
      )}
      {requested && ready && !selected && (
        <p role="status">Completion unavailable for this account.</p>
      )}
      {ownerId && completions.data && completions.data.length > 0 && (
        <section
          aria-label="Feedback test requests"
          hidden={!!requested && !ready}
          className="space-y-3"
        >
          <h2 className="text-xl font-medium">Test requests and history</h2>
          {completions.data.some(
            (row) => row.active && !row.confirmation_id,
          ) && <AboutApp />}
          {completions.data.map((completion) => (
            <CompletionCard
              key={`${ownerId}:${completion.completion_id}`}
              reportError={reportError}
              ownerId={ownerId}
              completion={completion}
              message={reportForCompletion(completion, data ?? [])?.message}
            />
          ))}
        </section>
      )}
      {isPending && (
        <p role="status" className="text-on-surface-variant">
          Loading your feedback…
        </p>
      )}
      {error && (
        <p
          role="alert"
          aria-label="Feedback list error"
          className="text-sm text-error"
        >
          {data ? 'Not refreshed' : 'Your feedback could not be loaded'}:{' '}
          {errorMessage(error)}.
        </p>
      )}
      {data && data.length === 0 && pending.length === 0 && (
        <p className="text-on-surface-variant">
          Nothing sent yet. What you send shows here, with what happened to it.
        </p>
      )}
      {data && data.length > 0 && (
        <ul className="space-y-3">
          {data.map((item) => (
            <li
              key={item.id}
              className="rounded-xl border border-outline/20 bg-surface p-4 space-y-2"
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="font-medium">
                  {FEEDBACK_KIND_LABELS[item.kind]}
                </span>
                <span className="text-on-surface-variant">
                  {format(new Date(item.created_at), 'MMM d, yyyy')}
                </span>
                <span
                  className={`ml-auto rounded-lg px-2 py-0.5 font-medium ${STATUS_STYLE[item.status]}`}
                >
                  {feedbackStatusLabel(item)}
                </span>
              </div>
              <p className="whitespace-pre-wrap break-words">{item.message}</p>
            </li>
          ))}
        </ul>
      )}
      {reachedLimit(data) && (
        <p className="text-sm text-on-surface-variant">
          Showing your latest {LIST_LIMIT.toLocaleString('en-US')}.
        </p>
      )}
    </div>
  );
}
